// Gas day-ahead-prijzen op Market Prices — rekenlogica van de tab "Gasprijzen".
//
// TWEE REEKSEN, TWEE VRAGEN. TTF (Nederlandse hub) en ZTP (Belgische hub), allebei
// in de EEX-notering. De tab bestaat om ze naast elkaar te leggen, en daar horen
// twee vragen bij die de cijfers zelf moeten beantwoorden:
//   1. verschillen de hubs van elkaar? → `gasVergelijkingen` zet ze per dag naast
//      elkaar en meldt expliciet wanneer ze IDENTIEK zijn;
//   2. welke reeks bestaat over welke periode? → `gasDekking` geeft eerste/laatste
//      dag én de ontbrekende dagen daartussen.
//
// De ICIS-Heren-noteringen (HERENTTF/HERENZTP) horen hier bewust NIET bij: Lynx
// antwoordt er 404 op met onze sleutel (gemeten 17-08-2026, elke periode, ook op
// GetPricesV2). Zie backend/src/util/gasPrijzen.js.
//
// KLEUR VOLGT DE HUB. Twee hues: TTF oranje, ZTP blauw. Het paar #d95926/#3987e5
// haalt ΔE 26,8 (protan) en 31,8 (normaal zicht) op zowel de lichte als de donkere
// achtergrond, gecontroleerd met de palet-validator — dus ook zonder kleurzicht uit
// elkaar te houden.
export const GAS_SERIES = [
  { type: 'EEXTTF', hub: 'TTF', bron: 'EEX', label: 'TTF (EEX)', kleur: '#d95926' },
  { type: 'EEXZTP', hub: 'ZTP', bron: 'EEX', label: 'ZTP (EEX)', kleur: '#3987e5' },
];

export const gasSerieMeta = (type) => GAS_SERIES.find((s) => s.type === type) || null;

const r3 = (v) => Math.round(v * 1000) / 1000;

// De reeksen van de API → rijen voor grafiek en tabel: één rij per dag, met per
// reeks een kolom. Een dag die in de ene reeks wel en in de andere niet voorkomt
// krijgt daar `null` — geen 0 en geen doorgetrokken waarde, zodat een gat in de
// grafiek een gat blijft.
export function gasRijen(series) {
  const perDag = new Map();
  for (const s of series || []) {
    for (const it of s.items || []) {
      if (!it || !it.date) continue;
      const rij = perDag.get(it.date) || { date: it.date };
      rij[s.type] = Number.isFinite(it.priceEurMwh) ? it.priceEurMwh : null;
      perDag.set(it.date, rij);
    }
  }
  const rijen = [...perDag.values()].sort((a, b) => a.date.localeCompare(b.date));
  // Elke reeks krijgt in élke rij een sleutel, ook waar ze niets heeft: Recharts
  // trekt met `connectNulls={false}` dan een onderbreking i.p.v. een rechte lijn
  // dwars over de ontbrekende dagen.
  const types = (series || []).map((s) => s.type);
  for (const rij of rijen) for (const t of types) if (!(t in rij)) rij[t] = null;
  return rijen;
}

// De paren die het vergelijken waard zijn. Met één notering blijft er één paar
// over: de twee hubs tegenover elkaar. De lijst blijft een lijst — een tweede
// notering erbij is dan één regel, niet een herschrijving.
const PAREN = [
  { id: 'eex-hub', a: 'EEXTTF', b: 'EEXZTP', label: 'ZTP t.o.v. TTF (EEX)' },
];

// Verschil b − a per GEMEENSCHAPPELIJKE dag. Alleen dagen waarop beide reeksen
// een prijs hebben tellen mee — anders zou een ontbrekende dag als een verschil
// van de volle prijs meetellen.
//
// `identiek` beantwoordt de vraag rechtstreeks: staan er in beide reeksen exact
// dezelfde getallen? De drempel is een halve duizendste, want de API levert drie
// decimalen; strikt op 0 toetsen zou op afrondingsruis stuklopen.
export function gasVergelijkingen(series) {
  const rijen = gasRijen(series);
  const uit = [];
  for (const p of PAREN) {
    const verschillen = [];
    for (const rij of rijen) {
      const a = rij[p.a], b = rij[p.b];
      if (!Number.isFinite(a) || !Number.isFinite(b)) continue;
      verschillen.push({ date: rij.date, verschil: b - a });
    }
    if (!verschillen.length) continue;   // niets te vergelijken → geen rij
    let som = 0, min = Infinity, max = -Infinity, maxAbs = 0, maxAbsDag = '';
    for (const v of verschillen) {
      som += v.verschil;
      if (v.verschil < min) min = v.verschil;
      if (v.verschil > max) max = v.verschil;
      if (Math.abs(v.verschil) > maxAbs) { maxAbs = Math.abs(v.verschil); maxAbsDag = v.date; }
    }
    uit.push({
      id: p.id,
      label: p.label,
      a: p.a,
      b: p.b,
      n: verschillen.length,
      gemiddelde: r3(som / verschillen.length),
      min: r3(min),
      max: r3(max),
      grootsteAfwijking: r3(maxAbs),
      grootsteAfwijkingDag: maxAbsDag,
      identiek: maxAbs < 0.0005,
    });
  }
  return uit;
}

// Over welke periode heeft deze reeks data, en welke dagen ontbreken daarbinnen?
// Ontbrekende dagen worden geteld en (beperkt) opgesomd: een reeks met gaten is
// iets anders dan een reeks die pas later begint, en dat verschil moet op het
// scherm te zien zijn.
export function gasDekking(serie, maxLijst = 5) {
  const items = (serie?.items || []).filter((i) => i && i.date && Number.isFinite(i.priceEurMwh));
  if (!items.length) return { dagen: 0, van: null, tot: null, ontbrekend: 0, ontbrekendeDagen: [] };
  const dagen = items.map((i) => i.date).sort();
  const van = dagen[0], tot = dagen[dagen.length - 1];
  const aanwezig = new Set(dagen);
  const ontbrekendeDagen = [];
  let ontbrekend = 0;
  for (let d = van; d <= tot; d = dagPlus(d, 1)) {
    if (aanwezig.has(d)) continue;
    ontbrekend++;
    if (ontbrekendeDagen.length < maxLijst) ontbrekendeDagen.push(d);
  }
  return { dagen: dagen.length, van, tot, ontbrekend, ontbrekendeDagen };
}

// Een dag verder in ISO, zonder tijdzone-effecten (alles op UTC-middernacht).
export function dagPlus(iso, n) {
  const [y, m, d] = String(iso).split('-').map(Number);
  const t = new Date(Date.UTC(y, m - 1, d + n));
  return `${t.getUTCFullYear()}-${String(t.getUTCMonth() + 1).padStart(2, '0')}-${String(t.getUTCDate()).padStart(2, '0')}`;
}
